import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    text = "text",
    decs = "decs",
}

const mapStateToProps = (state: ApplicationState) => {
    let { search } = getLocation(state);
    const { allowCreateAccount, allowGuestCheckout } = getSettingsCollection(state).accountSettings;
    let returnUrl = getReturnUrl(state);
    if (!returnUrl && state.components.signInOverlay.isOpen) {
        returnUrl = getLocation(state).pathname;
        if (!search) {
            search = `?returnUrl=${returnUrl}`;
        }
    }

    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const onePageCheckoutPageUrl = getPageLinkByPageType(state, "OnePageCheckoutPage")?.url;
    const referredFromOnePageCheckout = returnUrl === onePageCheckoutPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");
    return {
        allowCreateAccount,
        allowGuestCheckout:
            allowGuestCheckout &&
            !getCurrentUserIsGuest(state) &&
            (referredFromShipping || referredFromOnePageCheckout),
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
    };
};

const mapDispatchToProps = {
    signInAsGuest,
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.text]: string;
        [fields.decs]: string;
    };
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface SignInCreateNewAccountStyles {
    wrapper?: InjectableCss;
    signInCreateNewAccountGridContainerWrapper?: GridContainerProps;
    signInCreateNewAccountGridContainer?: GridContainerProps;
    signInCreateNewAccountTitleGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItem?: GridItemProps;
    signInCreateNewAccountTextGridItemsubContent?: GridItemProps;
    signInCreateNewAccountButtonGridItem?: GridItemProps;
    signInCreateNewAccountTitle?: TypographyProps;
    signInCreateNewAccountText?: TypographyProps;
    checkoutAsGuestButton?: ButtonPresentationProps;
    createNewAccountButton?: ButtonPresentationProps;
    topGridItem?: GridItemProps;
}
export const signInCreateNewAccountStyles: SignInCreateNewAccountStyles = {
    wrapper: {
        css: css`
            background-color: #f0f4f3;
            border-radius: 8px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        `,
    },
    signInCreateNewAccountGridContainerWrapper: {
        css: css`
            flex-grow: 1;
        `,
    },
    signInCreateNewAccountGridContainer: {
        css: css`
            display: flex;
            height: 100%;
            padding: 35px;
        `,
    },
    signInCreateNewAccountTitle: {
        variant: "h4",
        css: css`
            background-color: ${getColor("primary")};
            padding: 20px 35px;
            color: #fff;
            margin-bottom: 0;
        `,
    },
    signInCreateNewAccountTitleGridItem: {
        width: 12,
        css: css`
            display: block;
            padding: 0;
        `,
    },
    signInCreateNewAccountTextGridItem: {
        width: 12,
        css: css`
            font-weight: 700;
        `,
    },
    signInCreateNewAccountTextGridItemsubContent: {
        width: 12,
    },
    signInCreateNewAccountButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                            align-items: flex-start;
                        `,
                        null,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-end;
                        `,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-end;
                        `,
                        css`
                            flex-direction: row;
                            justify-content: space-between;
                            align-items: flex-end;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    checkoutAsGuestButton: {
        variant: "tertiary",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 1rem;
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    createNewAccountButton: {
        variant: "secondary",
        css: css`
            border-color: ${getColor("common.accent")};
            color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            width: 100%;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    topGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            padding-top: 35px;
        `,
    },
    signInCreateNewAccountText: {
        fontFamily: "LatoBold",
    },
};

/**
 * @deprecated Use signInCreateNewAccountStyles instead.
 */
const StyledForm = getStyledWrapper("form");
export const signInCreateNewAccount = signInCreateNewAccountStyles;
const styles = signInCreateNewAccountStyles;

const SignInCreateNewAccount: FC<Props> = ({
    history,
    fields,
    allowCreateAccount,
    allowGuestCheckout,
    returnUrl,
    createAccountUrl,
    signInAsGuest,
}) => {
    if (!allowCreateAccount) {
        return null;
    }

    const handleCheckoutAsGuest = () => {
        if (!returnUrl) {
            return;
        }

        signInAsGuest({ returnUrl });
    };

    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.signInCreateNewAccountTitle}>
                {translate("Don't have a registered account?")}
            </Typography>
            <StyledWrapper {...styles.signInCreateNewAccountGridContainerWrapper}>
                <GridContainer {...styles.signInCreateNewAccountGridContainer}>
                    <GridItem {...styles.topGridItem}>
                        <Typography variant="h4" {...styles.signInCreateNewAccountText}>
                            {fields.text}
                        </Typography>
                        <Typography>{fields.decs}</Typography>
                    </GridItem>
                    <GridItem {...styles.signInCreateNewAccountButtonGridItem}>
                        {allowGuestCheckout && (
                            <Button
                                {...styles.checkoutAsGuestButton}
                                onClick={handleCheckoutAsGuest}
                                data-test-selector="signInCreateNewAccount_checkoutAsGuest"
                            >
                                {translate("Checkout as Guest")}
                            </Button>
                        )}
                        {createAccountUrl && (
                            <Button
                                {...styles.createNewAccountButton}
                                onClick={handleCreateNewAccount}
                                data-test-selector="signInCreateNewAccount_createNewAccount"
                            >
                                {translate("Create An Account")}
                            </Button>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(SignInCreateNewAccount)),
    definition: {
        allowedContexts: ["SignInPage"],
        group: "Sign In",
        icon: "User",
        fieldDefinitions: [
            {
                name: fields.text,
                displayName: "Main Text",
                editorTemplate: "MultilineTextField",
                defaultValue: "Create an account to checkout faster, view order history, save product lists, and more!",
                fieldType: "Translatable",
            },
            {
                name: fields.decs,
                displayName: "Sub Heading Text",
                editorTemplate: "MultilineTextField",
                defaultValue:
                    "To become a registered user or practitioner or retailer to purchase product online with discounted price and get access to our resources respectively, click on the 'Create an Account' button and complete the form.",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
